import React, { useState, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography,
} from '@mui/material';
import { CheckCircleOutline } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google';
import { authService } from '../services/api';
import { useApp } from '../contexts/AppContext';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID || '';

const normalizeOAuthUser = (user: any) => {
  const role = (user.roles?.[0] || user.role || '').toLowerCase();
  return {
    id: user.id,
    name: user.displayName || user.name || 'Utente',
    email: user.email,
    companyId: user.companyId || '',
    role: role === 'customer' ? 'client' : role || 'viewer',
  };
};

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const { login: appLogin } = useApp();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');

  // Form fields
  const [companyName, setCompanyName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [vatNumber, setVatNumber] = useState('');

  // OAuth company name dialog
  const [oauthDialog, setOauthDialog] = useState(false);
  const [oauthCompanyName, setOauthCompanyName] = useState('');
  const [oauthPendingData, setOauthPendingData] = useState<{
    provider: 'google' | 'apple';
    idToken: string;
    displayName?: string;
  } | null>(null);

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Le password non coincidono');
      return;
    }
    if (password.length < 8) {
      setError('La password deve avere almeno 8 caratteri');
      return;
    }

    setLoading(true);
    try {
      await authService.register({
        email,
        password,
        displayName,
        companyName,
        vatNumber: vatNumber || undefined,
      });
      setSuccess(true);
      setRegisteredEmail(email);
    } catch (err: any) {
      const msg = err.response?.data?.error?.message || 'Errore durante la registrazione';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleOAuthLogin = useCallback(async (
    provider: 'google' | 'apple',
    idToken: string,
    displayName?: string
  ) => {
    setError('');
    setLoading(true);
    try {
      let response;
      if (provider === 'google') {
        response = await authService.googleLogin(idToken);
      } else {
        response = await authService.appleLogin(idToken, displayName);
      }

      const data = response?.data || response;

      if (data?.needsRegistration) {
        // Need company name — show dialog
        setOauthPendingData({ provider, idToken, displayName: data.displayName || displayName });
        setOauthCompanyName('');
        setOauthDialog(true);
        setLoading(false);
        return;
      }

      // Login successful
      if (data?.token) {
        localStorage.setItem('authToken', data.token);
        if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);
        if (data.user) localStorage.setItem('user', JSON.stringify(normalizeOAuthUser(data.user)));
        window.location.href = '/dashboard';
      }
    } catch (err: any) {
      const msg = err.response?.data?.error?.message || `Errore login ${provider}`;
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleOAuthWithCompany = async () => {
    if (!oauthPendingData || !oauthCompanyName.trim()) return;

    setOauthDialog(false);
    setLoading(true);
    setError('');

    try {
      let response;
      if (oauthPendingData.provider === 'google') {
        response = await authService.googleLogin(oauthPendingData.idToken, oauthCompanyName.trim());
      } else {
        response = await authService.appleLogin(
          oauthPendingData.idToken,
          oauthPendingData.displayName,
          oauthCompanyName.trim()
        );
      }

      const data = response?.data || response;
      if (data?.token) {
        localStorage.setItem('authToken', data.token);
        if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);
        if (data.user) localStorage.setItem('user', JSON.stringify(normalizeOAuthUser(data.user)));
        window.location.href = '/dashboard';
      }
    } catch (err: any) {
      const msg = err.response?.data?.error?.message || 'Errore durante la registrazione';
      setError(msg);
    } finally {
      setLoading(false);
      setOauthPendingData(null);
    }
  };

  if (success) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'grid',
          placeItems: 'center',
          px: 2,
          py: 3,
          background: 'transparent',
        }}
      >
        <Card
          sx={{
            width: '100%',
            maxWidth: 460,
            borderRadius: 4,
            border: '1px solid rgba(168, 191, 255, 0.22)',
            background: 'linear-gradient(160deg, rgba(16, 27, 44, 0.95) 0%, rgba(13, 21, 35, 0.98) 100%)',
            boxShadow: '0 26px 70px rgba(3, 8, 20, 0.56)',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ height: 5, background: 'var(--gradient-main)' }} />
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <CheckCircleOutline sx={{ fontSize: 56, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
              Controlla la tua email
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Abbiamo inviato un link di verifica a <strong>{registeredEmail}</strong>.
              <br />
              Clicca sul link per attivare il tuo account.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate('/login')}
              sx={{ mr: 1 }}
            >
              Vai al Login
            </Button>
            <Button
              variant="text"
              onClick={async () => {
                try {
                  await authService.resendVerification(registeredEmail);
                  setError('');
                } catch {
                  // ignore
                }
              }}
              sx={{ color: 'var(--accent-cyan)' }}
            >
              Rinvia email
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        px: 2,
        py: 3,
        background: 'transparent',
      }}
    >
      <Card
        sx={{
          width: '100%',
          maxWidth: 460,
          borderRadius: 4,
          border: '1px solid var(--border-color)',
          background: 'var(--surface)',
          boxShadow: 'var(--shadow-card)',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ height: 5, background: 'var(--gradient-main)' }} />
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1, mb: 2.4 }}>
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: 2.4,
                background: 'var(--gradient-main)',
                color: '#FFFFFF',
                display: 'grid',
                placeItems: 'center',
                fontWeight: 900,
                letterSpacing: '-0.03em',
              }}
            >
              CM
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                Registrati
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Crea il tuo account aziendale
              </Typography>
            </Box>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleRegister} sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
            <TextField
              fullWidth
              label="Nome Azienda"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              required
              disabled={loading}
              size="small"
            />
            <TextField
              fullWidth
              label="Nome e Cognome"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              required
              disabled={loading}
              size="small"
            />
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
              size="small"
            />
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
              size="small"
              helperText="Minimo 8 caratteri"
            />
            <TextField
              fullWidth
              label="Conferma Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              disabled={loading}
              size="small"
            />
            <TextField
              fullWidth
              label="P.IVA (opzionale)"
              value={vatNumber}
              onChange={(e) => setVatNumber(e.target.value)}
              disabled={loading}
              size="small"
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 1, py: 1.1 }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={22} /> : 'Registrati'}
            </Button>
          </Box>

          <Divider sx={{ my: 2.5, color: 'var(--text-muted)', fontSize: '0.78rem' }}>
            oppure
          </Divider>

          {/* Google Sign-In */}
          {GOOGLE_CLIENT_ID && (
            <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
                <GoogleLogin
                  onSuccess={(credentialResponse) => {
                    if (credentialResponse.credential) {
                      handleOAuthLogin('google', credentialResponse.credential);
                    }
                  }}
                  onError={() => setError('Errore durante il login con Google')}
                  text="continue_with"
                  shape="rectangular"
                  width="350"

                />
              </Box>
            </GoogleOAuthProvider>
          )}

          {/* Apple Sign-In button */}
          <Button
            fullWidth
            variant="outlined"
            onClick={() => {
              // Apple Sign In via JS SDK
              const w = window as any;
              if (w.AppleID) {
                w.AppleID.auth.signIn().then((res: any) => {
                  const token = res?.authorization?.id_token;
                  const name = res?.user
                    ? `${res.user.name?.firstName || ''} ${res.user.name?.lastName || ''}`.trim()
                    : undefined;
                  if (token) handleOAuthLogin('apple', token, name);
                }).catch(() => setError('Errore durante il login con Apple'));
              } else {
                setError('Apple Sign-In non disponibile');
              }
            }}
            disabled={loading}
            sx={{
              backgroundColor: '#000',
              color: '#fff',
              borderColor: 'rgba(255,255,255,0.3)',
              py: 1,
              fontWeight: 600,
              fontSize: '0.88rem',
              textTransform: 'none',
              '&:hover': { backgroundColor: '#1a1a1a', borderColor: 'rgba(255,255,255,0.5)' },
            }}
          >
            Continua con Apple
          </Button>

          <Typography
            variant="body2"
            align="center"
            sx={{ mt: 2.5, color: 'var(--text-secondary)' }}
          >
            Hai gia un account?{' '}
            <Box
              component="span"
              onClick={() => navigate('/login')}
              sx={{ color: 'var(--accent-cyan)', cursor: 'pointer', fontWeight: 600 }}
            >
              Accedi
            </Box>
          </Typography>
        </CardContent>
      </Card>

      {/* Dialog for OAuth company name */}
      <Dialog
        open={oauthDialog}
        onClose={() => { setOauthDialog(false); setOauthPendingData(null); }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'var(--menu-bg)',
            border: '1px solid var(--border-color)',
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Completa la registrazione</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Per completare la registrazione, inserisci il nome della tua azienda.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Nome Azienda"
            value={oauthCompanyName}
            onChange={(e) => setOauthCompanyName(e.target.value)}
            size="small"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setOauthDialog(false); setOauthPendingData(null); }}>
            Annulla
          </Button>
          <Button
            variant="contained"
            onClick={handleOAuthWithCompany}
            disabled={!oauthCompanyName.trim()}
          >
            Registrati
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RegisterPage;
